import {
  componentKey,
  eligibleTechniques,
  textValue as text
} from '../component/component.model'

export function readOverviewList (value, kind, companyId) {
  if (!Array.isArray(value))
    throw new Error(kind + ' API must return an array.')
  const seen = new Set()
  let excluded = 0
  const rows = value.filter(row => {
    if (!row || typeof row !== 'object' || Array.isArray(row))
      throw new Error(kind + ' contains an invalid record.')
    if (row.company_id != null && text(row.company_id) !== text(companyId)) {
      excluded++
      return false
    }
    return true
  })
  for (const row of rows) {
    let key
    if (kind === 'sites') {
      if (!text(row.site_id)) throw new Error('A site is missing its ID.')
      key = text(row.site_id)
    } else if (kind === 'assets') {
      if (!text(row.site_id) || !text(row.asset_id))
        throw new Error('An asset is missing its site or asset ID.')
      key = JSON.stringify([text(row.site_id), text(row.asset_id)])
    } else if (kind === 'components') {
      key = componentKey(row)
      if (!text(row.id) && !text(row.rds_code))
        throw new Error('A component has no record ID or RDS code.')
    } else {
      // No ID: retain the returned record, without assuming it is a duplicate.
      key = text(row.id) ? text(row.id) : null
    }
    if (key && seen.has(key))
      throw new Error(kind + ' contains duplicate record identities.')
    if (key) seen.add(key)
  }
  return { rows, excluded }
}

const assetKey = row => JSON.stringify([text(row.site_id), text(row.asset_id)])
const codeKey = row =>
  JSON.stringify([
    text(row.site_id),
    text(row.asset_id),
    text(row.rds_code).toLowerCase()
  ])
const emptyStats = () => ({ total: 0, open: 0, closed: 0, unknown: 0 })
function add (stats, row) {
  stats.total++
  const status = text(row.open_closed).toLowerCase()
  stats[
    status === 'open' ? 'open' : status === 'closed' ? 'closed' : 'unknown'
  ]++
}
function increment (map, key) {
  map.set(key, (map.get(key) || 0) + 1)
}
function addGroup (map, key, row) {
  if (!map.has(key)) map.set(key, emptyStats())
  add(map.get(key), row)
}
function scoped (rows, siteId, assetId) {
  return rows === null
    ? null
    : rows.filter(
        row =>
          (!siteId || text(row.site_id) === siteId) &&
          (!assetId || text(row.asset_id) === assetId)
      )
}

export function buildOverview (
  { sites, assets, components, anomalies },
  siteId = '',
  assetId = ''
) {
  siteId = text(siteId)
  assetId = text(assetId)
  const level = assetId ? 'components' : siteId ? 'assets' : 'sites'
  const siteMap = new Map((sites || []).map(row => [text(row.site_id), row]))
  const assetMap = new Map((assets || []).map(row => [assetKey(row), row]))
  const selectedSite = siteMap.get(siteId)
  const selectedAsset = assetMap.get(JSON.stringify([siteId, assetId]))
  const invalidScope =
    assetId && !siteId
      ? 'Choose a site for this asset.'
      : siteId && sites !== null && !selectedSite
      ? 'The selected site is not in the returned catalogue.'
      : assetId && assets !== null && !selectedAsset
      ? 'The selected asset is not registered to this site.'
      : ''
  const assetsPerSite = new Map(),
    componentsPerSite = new Map(),
    componentsPerAsset = new Map()
  const codes = new Map(),
    siteStats = new Map(),
    assetStats = new Map(),
    componentStats = new Map()
  for (const row of assets || []) increment(assetsPerSite, text(row.site_id))
  for (const row of components || []) {
    increment(componentsPerSite, text(row.site_id))
    increment(componentsPerAsset, assetKey(row))
    if (text(row.site_id) && text(row.asset_id) && text(row.rds_code))
      increment(codes, codeKey(row))
  }
  for (const row of anomalies || []) {
    addGroup(siteStats, text(row.site_id), row)
    addGroup(assetStats, assetKey(row), row)
    if (text(row.rds_component))
      addGroup(
        componentStats,
        codeKey({ ...row, rds_code: row.rds_component }),
        row
      )
  }
  const scopedAnomalies = scoped(anomalies, siteId, assetId)
  const totals =
    scopedAnomalies === null
      ? null
      : scopedAnomalies.reduce((result, row) => {
          add(result, row)
          return result
        }, emptyStats())
  const source =
    level === 'sites' ? sites : level === 'assets' ? assets : components
  const candidates =
    level === 'sites' ? source : scoped(source, siteId, assetId)
  const rows = (candidates || []).map(raw => {
    const site = text(raw.site_id),
      asset = level === 'sites' ? '' : text(raw.asset_id)
    const code = text(raw.rds_code)
    const ambiguous =
      level === 'components' && (!code || codes.get(codeKey(raw)) !== 1)
    const statsMap =
      level === 'sites'
        ? siteStats
        : level === 'assets'
        ? assetStats
        : componentStats
    const key =
      level === 'sites'
        ? site
        : level === 'assets'
        ? assetKey(raw)
        : codeKey(raw)
    const stats =
      anomalies === null || ambiguous ? null : statsMap.get(key) || emptyStats()
    const health =
      level === 'sites'
        ? text(raw.WFHealthStatus)
        : level === 'assets'
        ? text(raw.WTHealthStatus)
        : ''
    return {
      raw,
      id: level === 'components' ? componentKey(raw) : key,
      siteId: site,
      assetId: asset,
      label:
        level === 'sites'
          ? text(raw.WindFarm) || site
          : level === 'assets'
          ? text(raw.Asset) || asset
          : code || 'Component without RDS code',
      description:
        level === 'components'
          ? text(raw.description)
          : level === 'assets'
          ? text(selectedSite?.WindFarm) || site
          : site,
      health: health || 'Not recorded',
      tone: ['excellent', 'good', 'warning', 'poor', 'critical'].includes(
        health.toLowerCase()
      )
        ? health.toLowerCase()
        : 'unknown',
      lastInspection:
        level === 'sites'
          ? text(raw.WFMostRecentInspection)
          : level === 'assets'
          ? text(raw.WTMostRecentInspection)
          : '',
      assets: assets === null ? null : assetsPerSite.get(site) || 0,
      components:
        components === null
          ? null
          : (level === 'sites'
              ? componentsPerSite.get(site)
              : componentsPerAsset.get(assetKey(raw))) || 0,
      subsystem: text(raw.subsystem) || 'Not recorded',
      techniques: eligibleTechniques(raw),
      stats,
      ambiguous,
      attention:
        stats === null
          ? ambiguous
            ? 'Component link ambiguous'
            : 'Anomaly data unavailable'
          : stats.open
          ? 'Has open records'
          : 'No open records'
    }
  })
  // Records remain in totals even when they cannot be assigned to a visible child.
  let unassigned = null
  if (scopedAnomalies !== null && source !== null) {
    const childKeys = new Set(rows.map(row => row.id))
    unassigned = scopedAnomalies.filter(row => {
      if (level === 'sites') return !childKeys.has(text(row.site_id))
      if (level === 'assets') return !childKeys.has(assetKey(row))
      const key = codeKey({ ...row, rds_code: row.rds_component })
      return !text(row.rds_component) || codes.get(key) !== 1
    }).length
  }
  const unlistedAssets =
    level === 'sites' && sites !== null && assets !== null
      ? assets.filter(row => !siteMap.has(text(row.site_id))).length
      : null
  const scopeComponents = scoped(components, siteId, assetId)
  const unlistedComponents =
    scopeComponents !== null && assets !== null && sites !== null
      ? scopeComponents.filter(
          row => !siteMap.has(text(row.site_id)) || !assetMap.has(assetKey(row))
        ).length
      : null
  return {
    level,
    rows,
    totals,
    unassigned,
    unlistedAssets,
    unlistedComponents,
    invalidScope,
    siteLabel: text(selectedSite?.WindFarm) || siteId,
    assetLabel: text(selectedAsset?.Asset) || assetId,
    componentCount: scopeComponents === null ? null : scopeComponents.length,
    childCount: source === null ? null : rows.length
  }
}

const attentionFilter = {
  key: 'attention',
  label: 'Anomaly records',
  values: row => [row.attention]
}
export const locationOverviewFilters = [
  { key: 'health', label: 'Recorded health', values: row => [row.health] },
  attentionFilter
]
export const componentOverviewFilters = [
  { key: 'subsystem', label: 'Subsystem', values: row => [row.subsystem] },
  {
    key: 'technique',
    label: 'Eligible technique',
    values: row => row.techniques
  },
  attentionFilter
]
export const overviewSearchText = row =>
  [
    row.label,
    row.description,
    row.siteId,
    row.assetId,
    row.subsystem,
    ...row.techniques
  ].join(' ')
export function overviewColumns (level) {
  const identity = {
    key: 'label',
    label:
      level === 'sites'
        ? 'Site'
        : level === 'assets'
        ? 'Asset'
        : 'RDS-PP component',
    value: row => row.label
  }
  const numeric = (key, label, value) => ({ key, label, value, numeric: true })
  const stats = [
    numeric('open', 'Open records', row => row.stats?.open ?? null),
    numeric('closed', 'Closed records', row => row.stats?.closed ?? null),
    numeric(
      'unknown',
      'Other / unknown status',
      row => row.stats?.unknown ?? null
    )
  ]
  return level === 'components'
    ? [
        identity,
        { key: 'subsystem', label: 'Subsystem', value: row => row.subsystem },
        {
          key: 'technique',
          label: 'Eligible techniques',
          value: row => row.techniques.join(' · ')
        },
        ...stats
      ]
    : [
        identity,
        { key: 'health', label: 'Recorded health', value: row => row.health },
        ...(level === 'sites'
          ? [numeric('assets', 'Assets', row => row.assets)]
          : []),
        numeric('components', 'Components', row => row.components),
        ...stats,
        {
          key: 'lastInspection',
          label: 'Catalogue last inspection',
          value: row => row.lastInspection
        }
      ]
}
export function sortOverview (rows, sort, columns) {
  const column = columns.find(item => item.key === sort.key) || columns[0]
  const direction = sort.direction === 'desc' ? -1 : 1
  return [...rows].sort((a, b) => {
    const left = column.value(a),
      right = column.value(b)
    const missingLeft = left === null || left === '',
      missingRight = right === null || right === ''
    if (missingLeft || missingRight) {
      if (missingLeft !== missingRight) return missingLeft ? 1 : -1
      return a.id.localeCompare(b.id)
    }
    const difference = column.numeric
      ? left - right
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
    return direction * difference || a.id.localeCompare(b.id)
  })
}
